// src/components/mdx/client/visualizations/SignalNoiseChart.client.tsx

/**
 * Signal vs. Noise Interactive Chart
 * 
 * This component visualizes the concept of signal vs. noise in data analysis,
 * demonstrating how to distinguish meaningful patterns from random fluctuations.
 * It features interactive controls to adjust signal-to-noise ratio, signal frequency,
 * and smoothing techniques to help users understand how to unmask true insights.
 * 
 * OPTIMIZATION NOTE:
 * This component uses centralized imports from '~/lib/charts' instead of direct imports
 * from 'recharts'. This approach provides several bundle optimization benefits:
 * 
 * 1. Shared module chunking - All chart components share the same recharts code chunks
 * 2. Enhanced tree-shaking - More efficient dead code elimination across the codebase
 * 3. Import cost amortization - The fixed overhead cost of importing recharts is shared
 * 4. Deduplication - Better handling of nested dependencies within recharts
 * 5. Consistent code splitting - Creates cleaner boundaries for code splitting
 * 
 * As our application contains multiple visualizations, this approach significantly
 * reduces the overall bundle size compared to direct imports from 'recharts'.
 * 
 * ACCESSIBILITY NOTE:
 * The accessibilityLayer prop is explicitly set to false for Recharts 3.0 compatibility.
 * Recharts 3.0 introduced performance issues with the accessibility layer enabled,
 * causing significant rendering delays and memory overhead in complex visualizations.
 * 
 * PERFORMANCE OPTIMIZATIONS:
 * 1. ResponsiveContainer debounce - Prevents constant dimension recalculations during resize
 * 2. LineChart throttleDelay - Reduces mouse move event processing frequency
 * 3. Component-based tooltip - Passes component reference instead of JSX element
 */

"use client"

import {
  forwardRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useTransition,
  type ChangeEvent,
  type ReactNode,
  type FC
} from "react"
import {
  Line,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from "~/lib/charts/client"
import type { ValueType, NameType, TooltipContentType } from "~/lib/charts/types"
import {
  applyExponentialSmoothing,
  applyMedianFilter,
  calculateMovingAverage,
  type SignalNoiseDataPoint
} from "~/components/mdx/client/visualizations/signalNoiseMath"
import {
  getOrCreateTooltipWrapper,
  type CustomTooltipProps as TooltipWrapperProps
} from "~/lib/charts/tooltip"
import {
  createDeterministicRandom,
  hashSeed,
  type RandomSource
} from "~/lib/utils/random"
import { ChartVisibilityBoundary } from "~/components/mdx/client/visualizations/ChartVisibilityBoundary.client"

/**
 * Props for the ChartContainer component
 * @interface ChartContainerProps
 * @extends React.HTMLAttributes<HTMLDivElement>
 * @property {object} [config] - Configuration for chart series
 */
interface ChartContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  config?: Record<string, {
    label: string
    color: string
  }>
}

/**
 * A container component for charts with forwarded refs
 */
const ChartContainer = forwardRef<HTMLDivElement, ChartContainerProps>(
  ({ className, ...props }, ref) => {
    return (
      <div className={className} {...props} ref={ref}>
        {props.children}
      </div>
    )
  }
)
ChartContainer.displayName = "ChartContainer"

/**
 * Data point structure for time series data
 * 
 * @interface DataPoint
 * @property {number} x - The x-axis position/time point
 * @property {number} pureSignal - The underlying true signal value
 * @property {number} noisySignal - Signal with added random noise
 * @property {number} [movingAverage] - Optional moving average value
 * @property {number} [exponentialSmoothing] - Optional exponentially smoothed value
 * @property {number} [medianFilter] - Optional median filtered value
 */
type DataPoint = SignalNoiseDataPoint

const NUM_POINTS = 100

/**
 * Enum for available smoothing techniques
 * 
 * @enum {string} (none, movingAverage, exponentialSmoothing, medianFilter)
 */
enum SmoothingTechnique {
  NONE = "none",
  MOVING_AVERAGE = "movingAverage",
  EXPONENTIAL_SMOOTHING = "exponentialSmoothing",
  MEDIAN_FILTER = "medianFilter"
}

/**
 * Enum for signal patterns
 * 
 * @enum {string} (sine, step, trend, cyclical, seasonal)
 */
enum SignalPattern {
  SINE = "sine",
  STEP = "step",
  TREND = "trend",
  CYCLICAL = "cyclical",
  SEASONAL = "seasonal"
}

/**
 * Enum for noise types to create more realistic and varied noise patterns
 * 
 * @enum {string} (gaussian, heavyTailed, clustered, mixed)
 */
enum NoiseType {
  GAUSSIAN = "gaussian",
  HEAVY_TAILED = "heavyTailed",
  CLUSTERED = "clustered",
  MIXED = "mixed"
}

/**
 * Generates combined signal and noise data based on parameters
 * 
 * @param {number} numPoints - Number of data points to generate
 * @param {number} signalToNoiseRatio - Ratio of signal strength to noise (0-100)
 * @param {number} frequency - Frequency of the signal wave
 * @param {SignalPattern} pattern - Type of signal pattern to generate
 * @param {RandomSource} rng - Deterministic random number generator
 * @returns {DataPoint[]} Array of data points
 */
const generateData = (
  numPoints: number,
  signalToNoiseRatio: number,
  frequency: number,
  pattern: SignalPattern,
  rng: RandomSource
): DataPoint[] => {
  // Normalize SNR from percentage (0-100) to a ratio for calculations
  const snr = signalToNoiseRatio / 100
  
  // Define base noise amplitude (inverse relationship with SNR)
  // When SNR is 0%, noise amplitude is maximal (5)
  // When SNR is 100%, noise amplitude is minimal (0.1)
  const baseNoiseAmplitude = 5 * (1 - snr) + 0.1
  
  // Define signal amplitude (direct relationship with SNR)
  // When SNR is 0%, signal amplitude is minimal (0.1)
  // When SNR is 100%, signal amplitude is maximal (3)
  const signalAmplitude = 0.1 + 2.9 * snr
  
  // Adjust frequency coefficient to make changes more visible
  const frequencyFactor = 0.05 + (frequency / 100) * 0.25
  
  // Select a noise type randomly or based on some condition
  // This creates more varied and realistic noise patterns
  const noiseTypes = [
    NoiseType.GAUSSIAN,
    NoiseType.HEAVY_TAILED,
    NoiseType.CLUSTERED,
    NoiseType.MIXED
  ]
  const selectedNoiseType = noiseTypes[Math.floor(rng() * noiseTypes.length)] ?? NoiseType.GAUSSIAN
  
  // Generate noise with occasional outliers and bursts
  const generateNoise = (index: number, noiseType: NoiseType): number => {
    let cyclePosition
    
    switch (noiseType) {
      case NoiseType.GAUSSIAN:
        // Standard Gaussian noise
        return (rng() * 2 - 1) * baseNoiseAmplitude
        
      case NoiseType.HEAVY_TAILED:
        // Heavy-tailed distribution for more extreme outliers
        if (rng() < 0.05) { // 5% chance of outlier
          return (rng() * 2 - 1) * baseNoiseAmplitude * 3
        }
        return (rng() * 2 - 1) * baseNoiseAmplitude
        
      case NoiseType.CLUSTERED:
        // Clustered noise (autocorrelated) - noise tends to persist
        cyclePosition = index % 20
        if (cyclePosition < 5) {
          return (rng() * 0.5 + 0.5) * baseNoiseAmplitude * 2
        } else if (cyclePosition < 10) {
          return (rng() * 0.5 - 1) * baseNoiseAmplitude * 2
        }
        return (rng() * 2 - 1) * baseNoiseAmplitude
        
      case NoiseType.MIXED:
        // Combination of different noise patterns
        if (rng() < 0.03) { // 3% chance of extreme outlier
          return (rng() * 2 - 1) * baseNoiseAmplitude * 4
        } else if (index % 15 < 3) { // Periodic noise bursts
          return (rng() * 2 - 1) * baseNoiseAmplitude * 2
        }
        return (rng() * 2 - 1) * baseNoiseAmplitude
        
      default:
        return (rng() * 2 - 1) * baseNoiseAmplitude
    }
  }
  
  const data: DataPoint[] = []
  
  // Add some pre-noise to make the beginning less predictable
  let lastNoise = 0
  
  for (let i = 0; i < numPoints; i++) {
    // Calculate base x position
    const x = i
    
    // Generate pure signal based on selected pattern
    let pureSignal = 0
    
    switch (pattern) {
      case SignalPattern.SINE:
        // Simple sine wave
        pureSignal = Math.sin(i * frequencyFactor) * signalAmplitude + 5
        break
        
      case SignalPattern.STEP: {
        // Step function (jumps between values)
        const stepSize = Math.ceil(numPoints / (5 + (frequency / 20)))
        pureSignal = (Math.floor(i / stepSize) % 2 === 0 ? 3 : 7) * signalAmplitude / 3
        break
      }
        
      case SignalPattern.TREND: {
        // Linear trend with small oscillations
        const trendSlope = 10 / numPoints
        pureSignal = (i * trendSlope * signalAmplitude) + 
                     (Math.sin(i * frequencyFactor * 0.5) * signalAmplitude * 0.3) + 3
        break
      }
        
      case SignalPattern.CYCLICAL:
        // Combination of multiple sine waves of different frequencies
        pureSignal = (
          Math.sin(i * frequencyFactor) + 
          Math.sin(i * frequencyFactor * 0.5) * 0.5 +
          Math.sin(i * frequencyFactor * 0.2) * 1.5
        ) * signalAmplitude / 2 + 5
        break
        
      case SignalPattern.SEASONAL: {
        // Repeating pattern with distinct "seasons"
        const seasonLength = Math.ceil(numPoints / (4 + (frequency / 25)))
        const seasonPosition = i % seasonLength
        const seasonProgress = seasonPosition / seasonLength
        
        if (seasonProgress < 0.25) {
          // Spring - rising
          pureSignal = 3 + seasonProgress * 4 * signalAmplitude
        } else if (seasonProgress < 0.5) {
          // Summer - high with variations
          pureSignal = 7 * signalAmplitude / 3 + Math.sin(seasonPosition * 0.5) * 0.4 * signalAmplitude
        } else if (seasonProgress < 0.75) {
          // Fall - declining
          pureSignal = 7 - (seasonProgress - 0.5) * 4 * signalAmplitude
        } else {
          // Winter - low with variations
          pureSignal = 3 * signalAmplitude / 3 + Math.sin(seasonPosition * 0.5) * 0.3 * signalAmplitude
        }
        break
      }
    }
    
    // Generate noise with some autocorrelation (noise tends to persist)
    // This creates more realistic noise patterns that can mask signals
    const noise = generateNoise(i, selectedNoiseType ?? NoiseType.GAUSSIAN)
    
    // Add some autocorrelation to the noise (makes it more realistic)
    const autocorrelatedNoise = (noise * 0.7) + (lastNoise * 0.3)
    lastNoise = autocorrelatedNoise
    
    // Combine signal and noise
    const noisySignal = pureSignal + autocorrelatedNoise
    
    data.push({
      x,
      pureSignal,
      noisySignal
    })
  }
  
  return data
}

/**
 * Props for the custom tooltip component
 *
 * @interface SignalTooltipProps
 * @extends TooltipWrapperProps
 * @property {boolean} showPureSignal - Whether to show the pure signal in tooltip
 */
interface SignalTooltipProps extends TooltipWrapperProps {
  showPureSignal: boolean
}

/**
 * Custom tooltip component for the chart
 * 
 * @param {SignalTooltipProps} props - Component props
 * @returns {JSX.Element | null} Rendered tooltip or null if inactive
 */
const CustomTooltip = ({ active, payload, label, showPureSignal }: SignalTooltipProps): ReactNode => {
  if (active && payload?.length) {
    return (
      <div className="bg-gray-800/90 backdrop-blur-sm border border-gray-700 rounded-lg shadow-lg p-4">
        <p className="text-gray-300 font-medium mb-2">Point {label}</p>
        
        {payload.map((entry, idx) => {
          // Skip pure signal if not showing it
          if (!showPureSignal && entry.dataKey === "pureSignal") {
            return null
          }
          
          return (
            <p key={`value-${idx}`} className="text-sm text-gray-400">
              {entry.name}: <span className="font-medium" style={{ color: entry.color }}>{entry.value.toFixed(2)}</span>
            </p>
          )
        })}
      </div>
    )
  }
  return null
}

/**
 * Custom legend component that handles mobile responsiveness
 */
const CustomLegend = ({ payload, isMobile }: { 
  payload?: Array<{
    value: string
    type: string
    color: string
    dataKey: string
  }>
  isMobile: boolean
}): JSX.Element | null => {
  if (!payload?.length) return null
  
  return (
    <div className={`flex ${isMobile ? "flex-col gap-1" : "flex-wrap justify-center gap-4"} mb-4`}>
      {payload.map((entry, index) => (
        <div
          key={`legend-${index}`}
          className="flex items-center"
          data-testid={`signal-noise-legend-${entry.dataKey}`}
        >
          <div 
            className={`${isMobile ? "w-3 h-3" : "w-4 h-4"} rounded-full mr-2`}
            style={{ backgroundColor: entry.color }}
          />
          <span 
            className={`${isMobile ? "text-xs" : "text-sm"} text-gray-300 whitespace-nowrap`}
            style={{ color: entry.color }}
          >
            {entry.value}
          </span>
        </div>
      ))}
    </div>
  )
}

/**
 * Props for the SignalNoiseChartContent component
 * @interface SignalNoiseChartContentProps
 * @property {boolean} isActive - Indicates whether the chart is on-screen so we can throttle transitions
 */
interface SignalNoiseChartContentProps {
  isActive: boolean
}

/**
 * Signal vs. Noise Interactive Chart Content Component
 * Demonstrates how to distinguish meaningful patterns from random fluctuations
 * 
 * @param {SignalNoiseChartContentProps} props - Component props
 * @returns {JSX.Element} Rendered component
 */
function SignalNoiseChartContent({ isActive }: SignalNoiseChartContentProps): JSX.Element {
  // State for interactive controls
  const [signalToNoiseRatio, setSignalToNoiseRatio] = useState<number>(50)
  const [frequency, setFrequency] = useState<number>(30)
  const [smoothingTechnique, setSmoothingTechnique] = useState<SmoothingTechnique>(SmoothingTechnique.MOVING_AVERAGE)
  const [showPureSignal, setShowPureSignal] = useState<boolean>(true)
  const [patternType, setPatternType] = useState<SignalPattern>(SignalPattern.SINE)
  const [smoothingParameter, setSmoothingParameter] = useState<number>(10) // Window size for MA/Median, alpha for Exp
  
  // PERFORMANCE: useTransition allows us to mark state updates as non-urgent,
  // keeping the UI responsive during expensive re-renders
  const [, startTransition] = useTransition()
  const scheduleUpdate = useCallback(
    (callback: () => void) => {
      if (!isActive) {
        callback()
        return
      }
      startTransition(callback)
    },
    [isActive, startTransition]
  )
  
  // PERFORMANCE: useRef to store showPureSignal without causing tooltip re-creation
  // This prevents the tooltip component from being recreated on every render
  const showPureSignalRef = useRef<boolean>(showPureSignal)

  // Keep ref in sync with state
  useEffect(() => {
    showPureSignalRef.current = showPureSignal
  }, [showPureSignal])

  // PERFORMANCE: Responsive settings based on screen size using matchMedia API
  // matchMedia is more efficient than resize listeners for media query detection
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    // SSR-safe initialization - return false during server-side rendering
    if (typeof window === "undefined") {
      return false
    }

    // Use matchMedia for initial check - more efficient than checking window.innerWidth
    return window.matchMedia("(max-width: 768px)").matches
  })

  useEffect(() => {
    // Guard against SSR
    if (typeof window === "undefined") {
      return
    }

    // Create media query list for mobile breakpoint
    const mediaQuery = window.matchMedia("(max-width: 768px)")

    // Initial update to ensure state matches current viewport
    const updateMatches = (): void => {
      setIsMobile(mediaQuery.matches)
    }

    updateMatches()

    // Event handler for media query changes
    const handleChange = (event: MediaQueryListEvent): void => {
      setIsMobile(event.matches)
    }

    // PERFORMANCE: Use addEventListener if available (modern browsers),
    // fallback to addListener for older browsers
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange)
      return () => mediaQuery.removeEventListener("change", handleChange)
    }

    // Fallback for older browsers
    mediaQuery.addListener(handleChange)
    return () => mediaQuery.removeListener(handleChange)
  }, [])
  
  // PERFORMANCE: Generate data based on current parameters
  // useMemo prevents regenerating data on every render - only when dependencies change
  const rawData = useMemo(() => {
    const seed = hashSeed(
      "signal-noise",
      NUM_POINTS,
      signalToNoiseRatio,
      frequency,
      patternType
    )
    const rng = createDeterministicRandom(seed)
    return generateData(NUM_POINTS, signalToNoiseRatio, frequency, patternType, rng)
  }, [signalToNoiseRatio, frequency, patternType])
  
  // PERFORMANCE: Apply selected smoothing technique with optimized memoization
  // Returns the same reference when no smoothing is needed to prevent unnecessary re-renders
  const processedData = useMemo(() => {
    // Don't copy the array if no smoothing needed - return same reference
    if (smoothingTechnique === SmoothingTechnique.NONE) {
      return rawData
    }
    
    switch (smoothingTechnique) {
      case SmoothingTechnique.MOVING_AVERAGE:
        return calculateMovingAverage(rawData, smoothingParameter)
        
      case SmoothingTechnique.EXPONENTIAL_SMOOTHING: {
        // Convert parameter from 1-20 scale to 0.05-0.95 alpha value
        const alpha = 0.05 + (smoothingParameter - 1) * (0.90 / 19)
        return applyExponentialSmoothing(rawData, alpha)
      }
        
      case SmoothingTechnique.MEDIAN_FILTER: {
        // Convert parameter to odd window size between 3 and 21
        const windowSize = smoothingParameter * 2 - 1
        return applyMedianFilter(rawData, windowSize)
      }
        
      default:
        return rawData
    }
  }, [rawData, smoothingTechnique, smoothingParameter])

  // Pre-compute expensive values to prevent recalculation during renders
  const chartDimensions = useMemo(() => ({
    width: isMobile ? 375 : 800,
    height: isMobile ? 280 : 400,
    margin: isMobile ? 
      { top: 10, right: 10, left: 0, bottom: 10 } : 
      { top: 20, right: 15, left: 10, bottom: 20 }
  }), [isMobile])

  // Stable color palette to prevent style recalculation
  const colorPalette = useMemo(() => ({
    noisy: "rgb(249, 168, 212)", // pink-300
    smoothing: "rgb(52, 211, 153)", // emerald-400  
    pure: "rgb(129, 140, 248)", // indigo-400
    grid: "rgb(75, 85, 99)",
    text: "rgb(156, 163, 175)"
  }), [])

  // Get appropriate title for the smoothing parameter based on technique
  const smoothingParameterTitle = 
    smoothingTechnique === SmoothingTechnique.EXPONENTIAL_SMOOTHING 
      ? "Smoothing Factor (α)" 
      : "Window Size"
  
  // Get appropriate data key for the selected smoothing technique
  const smoothingDataKey = 
    smoothingTechnique === SmoothingTechnique.NONE 
      ? undefined 
      : smoothingTechnique
  
  // PERFORMANCE: Create legend data with all calculations inside useMemo
  // This prevents recalculation on every render and ensures all dependencies are tracked
  const legendData = useMemo(() => {
    // Calculate alpha value inside useMemo to avoid stale closures
    const alpha = smoothingTechnique === SmoothingTechnique.EXPONENTIAL_SMOOTHING 
      ? (0.05 + (smoothingParameter - 1) * (0.90 / 19)).toFixed(2)
      : undefined
    
    const data = [
      {
        value: "Noisy Data",
        type: "line",
        color: "rgb(249, 168, 212)",
        dataKey: "noisySignal"
      }
    ]
    
    if (smoothingTechnique !== SmoothingTechnique.NONE && smoothingDataKey) {
      const smoothingLabels = {
        [SmoothingTechnique.MOVING_AVERAGE]: isMobile ? `MA (${smoothingParameter})` : `Moving Average (n=${smoothingParameter})`,
        [SmoothingTechnique.EXPONENTIAL_SMOOTHING]: isMobile ? `ES (α=${alpha})` : `Exponential Smoothing (α=${alpha})`,
        [SmoothingTechnique.MEDIAN_FILTER]: isMobile ? `MF (${smoothingParameter * 2 - 1})` : `Median Filter (n=${smoothingParameter * 2 - 1})`
      }
      
      data.push({
        value: smoothingLabels[smoothingDataKey],
        type: "line",
        color: "rgb(52, 211, 153)",
        dataKey: smoothingDataKey
      })
    }
    
    if (showPureSignal) {
      data.push({
        value: "True Signal",
        type: "line",
        color: "rgb(129, 140, 248)",
        dataKey: "pureSignal"
      })
    }
    
    return data
  }, [smoothingTechnique, smoothingDataKey, smoothingParameter, showPureSignal, isMobile])

  // Calculate alpha value for display (if using exponential smoothing)
  const alphaValue = 
    smoothingTechnique === SmoothingTechnique.EXPONENTIAL_SMOOTHING 
      ? (0.05 + (smoothingParameter - 1) * (0.90 / 19)).toFixed(2)
      : undefined

  /**
   * Memoized tooltip content component
   * 
   * PERFORMANCE NOTE:
   * The accessibilityLayer prop is set to false to avoid Recharts 3.0 performance issues.
   * Recharts 3.0 introduced significant rendering overhead when accessibility layer is enabled,
   * particularly in charts with frequent updates or large datasets. This can cause:
   * - Increased render times (up to 3-5x slower)
   * - Memory leaks in some scenarios
   * - Janky animations and interactions
   * 
   * We maintain accessibility through alternative means:
   * - Semantic HTML structure with proper heading hierarchy
   * - ARIA labels on all interactive controls (sliders, selects, checkboxes)
   * - Keyboard navigation support for all controls
   * - Live regions (aria-live) for dynamic value updates
   * - Descriptive labels and helper text
   * - Custom tooltip with clear, readable content
   * 
   * PERFORMANCE OPTIMIZATION:
   * We pass the component directly to Recharts instead of creating a JSX element.
   * This prevents expensive React reconciliation on every mouse move, as Recharts
   * can efficiently clone the component with new props without triggering full re-renders.
   * 
   * Additional tooltip optimizations:
   * - shared={true}: Uses axis mode for more efficient data calculation per tick/index
   */
  const TooltipContent = useMemo(() => {
    // Create a component that passes showPureSignal to CustomTooltip
    const TooltipComponent: FC<TooltipWrapperProps> = props => (
      <CustomTooltip {...props} showPureSignal={showPureSignalRef.current} />
    )

    TooltipComponent.displayName = "SignalNoiseTooltip"

    // Use the cached tooltip wrapper for performance. TooltipComponent reads
    // showPureSignalRef.current when recharts renders the tooltip on hover (a
    // deferred render), so the ref read is never during THIS render.
    // eslint-disable-next-line react-hooks/refs -- ref is read in the deferred tooltip render, not here
    return getOrCreateTooltipWrapper(TooltipComponent)
  }, [])

  const handleSignalToNoiseRatioChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = Number(event.target.value)
    scheduleUpdate(() => {
      setSignalToNoiseRatio(nextValue)
    })
  }, [scheduleUpdate])

  const handleFrequencyChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = Number(event.target.value)
    scheduleUpdate(() => {
      setFrequency(nextValue)
    })
  }, [scheduleUpdate])

  const handleSmoothingParameterChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = Number(event.target.value)
    scheduleUpdate(() => {
      setSmoothingParameter(nextValue)
    })
  }, [scheduleUpdate])

  const handlePatternChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    const nextValue = event.target.value as SignalPattern
    scheduleUpdate(() => {
      setPatternType(nextValue)
    })
  }, [scheduleUpdate])

  const handleSmoothingTechniqueChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    const nextValue = event.target.value as SmoothingTechnique
    scheduleUpdate(() => {
      setSmoothingTechnique(nextValue)
    })
  }, [scheduleUpdate])

  const handleShowPureSignalChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setShowPureSignal(event.target.checked)
  }, [])
  
  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl sm:rounded-3xl shadow-2xl overflow-hidden">
      <div className="p-4 sm:p-8">
        <h2 className="text-xl sm:text-3xl font-bold mb-1 sm:mb-2 text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-pink-400">
          Signal vs. Noise Interactive Explorer
        </h2>
        <p className="text-sm sm:text-base text-gray-400 mb-3 sm:mb-6">
          Unmask true insights by distinguishing patterns from random fluctuations
        </p>
        
        {/* Controls Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm text-gray-400 mb-1">
                <span id="signal-to-noise-slider-label">Signal-to-Noise Ratio</span>
                <span
                  className="font-medium"
                  data-testid="signal-to-noise-value"
                  aria-live="polite"
                >
                  {signalToNoiseRatio}%
                </span>
              </div>
              <input
                id="signal-to-noise-slider"
                type="range"
                min="0"
                max="100"
                value={signalToNoiseRatio}
                onChange={handleSignalToNoiseRatioChange}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                aria-labelledby="signal-to-noise-slider-label"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={signalToNoiseRatio}
                data-testid="signal-to-noise-slider"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Pure Noise</span>
                <span>Pure Signal</span>
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-sm text-gray-400 mb-1">
                <span id="signal-frequency-slider-label">Signal Frequency</span>
                <span className="font-medium" data-testid="signal-frequency-value" aria-live="polite">{frequency}</span>
              </div>
              <input
                id="signal-frequency-slider"
                type="range"
                min="1"
                max="100"
                value={frequency}
                onChange={handleFrequencyChange}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                aria-labelledby="signal-frequency-slider-label"
                aria-valuemin={1}
                aria-valuemax={100}
                aria-valuenow={frequency}
                data-testid="signal-frequency-slider"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Low</span>
                <span>High</span>
              </div>
            </div>
            
            <div>
              <label htmlFor="pattern-type-select" className="block text-sm text-gray-400 mb-1">Pattern Type</label>
              <select
                id="pattern-type-select"
                value={patternType}
                onChange={handlePatternChange}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                aria-label="Select the underlying pattern"
              >
                <option value={SignalPattern.SINE}>Sine Wave</option>
                <option value={SignalPattern.STEP}>Step Function</option>
                <option value={SignalPattern.TREND}>Linear Trend</option>
                <option value={SignalPattern.CYCLICAL}>Cyclical Pattern</option>
                <option value={SignalPattern.SEASONAL}>Seasonal Pattern</option>
              </select>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="smoothing-technique-select" className="block text-sm text-gray-400 mb-1">Smoothing Technique</label>
              <select
                id="smoothing-technique-select"
                value={smoothingTechnique}
                onChange={handleSmoothingTechniqueChange}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                aria-label="Select smoothing technique"
              >
                <option value={SmoothingTechnique.NONE}>None</option>
                <option value={SmoothingTechnique.MOVING_AVERAGE}>Moving Average</option>
                <option value={SmoothingTechnique.EXPONENTIAL_SMOOTHING}>Exponential Smoothing</option>
                <option value={SmoothingTechnique.MEDIAN_FILTER}>Median Filter</option>
              </select>
            </div>
            
            {smoothingTechnique !== SmoothingTechnique.NONE && (
              <div>
                <div className="flex justify-between text-sm text-gray-400 mb-1">
                  <span>{smoothingParameterTitle}</span>
                  <span className="font-medium">
                    {smoothingTechnique === SmoothingTechnique.EXPONENTIAL_SMOOTHING 
                      ? `α=${alphaValue}` 
                      : smoothingTechnique === SmoothingTechnique.MOVING_AVERAGE 
                        ? smoothingParameter 
                        : smoothingParameter * 2 - 1}
                  </span>
                </div>
                <input
                  id="smoothing-parameter-slider"
                  type="range"
                  min="1"
                  max="20"
                  value={smoothingParameter}
                  onChange={handleSmoothingParameterChange}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                  aria-valuemin={1}
                  aria-valuemax={20}
                  aria-valuenow={smoothingParameter}
                  aria-label={smoothingParameterTitle}
                  data-testid="smoothing-parameter-slider"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  {smoothingTechnique === SmoothingTechnique.EXPONENTIAL_SMOOTHING ? (
                    <>
                      <span>More Responsive</span>
                      <span>More Smoothing</span>
                    </>
                  ) : (
                    <>
                      <span>Small Window</span>
                      <span>Large Window</span>
                    </>
                  )}
                </div>
              </div>
            )}
            
            <div className="flex items-center">
              <input
                id="show-pure-signal"
                type="checkbox"
                checked={showPureSignal}
                onChange={handleShowPureSignalChange}
                className="w-4 h-4 text-indigo-600 bg-gray-800 border-gray-700 rounded focus:ring-indigo-500"
                aria-label="Toggle true signal visibility"
              />
              <label htmlFor="show-pure-signal" className="ml-2 text-sm text-gray-400">
                Show True Signal (Hidden in Real Data)
              </label>
            </div>
          </div>
        </div>
        
        {/* Custom Legend */}
        <CustomLegend payload={legendData} isMobile={isMobile} />
        
        {/* Chart */}
        <ChartContainer
          config={{
            pureSignal: {
              label: "True Signal",
              color: "rgb(129, 140, 248)", // indigo-400
            },
            noisySignal: {
              label: "Noisy Data",
              color: "rgb(249, 168, 212)", // pink-300
            },
            ...(smoothingDataKey ? {
              [smoothingDataKey]: {
                label: {
                  [SmoothingTechnique.MOVING_AVERAGE]: `Moving Average (n=${smoothingParameter})`,
                  [SmoothingTechnique.EXPONENTIAL_SMOOTHING]: `Exponential Smoothing (α=${alphaValue})`,
                  [SmoothingTechnique.MEDIAN_FILTER]: `Median Filter (n=${smoothingParameter * 2 - 1})`
                }[smoothingDataKey],
                color: "rgb(52, 211, 153)", // emerald-400
              }
            } : {})
          }}
          className="w-full aspect-[4/3] sm:aspect-[16/9]"
        >
          <ResponsiveContainer 
            width="100%" 
            height="100%" 
            debounce={300} // Prevents constant dimension recalculations during resize (Recharts 3.0 defaults to 0 which is insane)
            initialDimension={{ width: 800, height: 400 }} // SSR hydration fix
          >
            <LineChart 
              data={processedData} 
              margin={chartDimensions.margin}
              accessibilityLayer={false} // Critical: Disable for performance (Recharts 3.0+)
              throttleDelay={50} // Reduces mouse move event processing frequency  
              syncId="signalNoiseChart" // Sync chart interactions across components
            >
              <CartesianGrid strokeDasharray="3 3" stroke={colorPalette.grid} opacity={0.2} />
              <XAxis
                dataKey="x"
                stroke={colorPalette.text}
                fontSize={isMobile ? 10 : 12}
                tickLine={false}
                axisLine={false}
                interval={isMobile ? 19 : 9} // Show fewer ticks on mobile
                label={isMobile ? undefined : {
                  value: "Time",
                  position: "insideBottom",
                  offset: -10,
                  fill: colorPalette.text,
                }}
              />
              <YAxis
                stroke={colorPalette.text}
                fontSize={isMobile ? 10 : 12}
                tickLine={false}
                axisLine={false}
                domain={["auto", "auto"]}
                width={isMobile ? 25 : 40}
                label={isMobile ? undefined : { 
                  value: "Value", 
                  angle: -90, 
                  position: "insideLeft", 
                  offset: 10, 
                  fill: "rgb(156, 163, 175)" 
                }}
              />
              <Tooltip 
                content={TooltipContent as TooltipContentType<ValueType, NameType>} 
                animationDuration={100}
                animationEasing="ease-out"
                shared={true}
                allowEscapeViewBox={{ x: false, y: false }} // Prevent tooltip from escaping the chart
                filterNull={false}
                includeHidden={false}
              />
              
              {/* Noisy Data Line */}
              <Line
                type="monotone"
                dataKey="noisySignal"
                stroke={colorPalette.noisy} // pink-300
                strokeWidth={isMobile ? 1 : 1.5}
                dot={false}
                name="Noisy Data"
                animationDuration={800}
                animationEasing="ease-out"
                connectNulls={false} // Better performance than connecting gaps
                activeDot={{ 
                  r: 4, 
                  fill: colorPalette.noisy, 
                  stroke: colorPalette.grid,
                  strokeWidth: 2 
                }}
              />
              
              {/* Smoothing Line (if applied) */}
              {smoothingTechnique !== SmoothingTechnique.NONE && smoothingDataKey && (
                <Line
                  type="monotone"
                  dataKey={smoothingDataKey}
                  stroke={colorPalette.smoothing} // emerald-400
                  strokeWidth={isMobile ? 2 : 3}
                  dot={false}
                  name={{
                    [SmoothingTechnique.MOVING_AVERAGE]: `Moving Average (n=${smoothingParameter})`,
                    [SmoothingTechnique.EXPONENTIAL_SMOOTHING]: `Exponential Smoothing (α=${alphaValue})`,
                    [SmoothingTechnique.MEDIAN_FILTER]: `Median Filter (n=${smoothingParameter * 2 - 1})`
                  }[smoothingDataKey]}
                  animationDuration={800}
                  animationEasing="ease-out"
                  connectNulls={false}
                />
              )}
              
              {/* Pure Signal Line (optional) */}
              {showPureSignal && (
                <Line
                  type="monotone"
                  dataKey="pureSignal"
                  stroke={colorPalette.pure} // indigo-400
                  strokeWidth={isMobile ? 1.5 : 2}
                  strokeDasharray="5 5"
                  dot={false}
                  name="True Signal"
                  animationDuration={800}
                  animationEasing="ease-out"
                  connectNulls={false}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
        
        {/* Explanations */}
        <div className="mt-6 bg-gray-800/50 rounded-lg p-4 text-sm text-gray-300">
          <h3 className="text-lg font-semibold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 to-pink-300">What You&apos;re Seeing</h3>
          <div className="space-y-4">
            <div className="flex items-start border-l-2 border-pink-300/70 pl-3 py-1">
              <div className="flex-1">
                <h4 className="font-medium text-pink-300 mb-1 flex items-center">
                  <span className="h-2 w-2 rounded-full bg-pink-300 mr-2"></span>
                  Noisy Data
                </h4>
                <p className="text-gray-300">
                  Represents what we typically collect in marketing analytics—a combination of meaningful patterns hidden within random fluctuations.
                </p>
              </div>
            </div>
            
            {smoothingTechnique !== SmoothingTechnique.NONE && (
              <div className="flex items-start border-l-2 border-emerald-400/70 pl-3 py-1">
                <div className="flex-1">
                  <h4 className="font-medium text-emerald-400 mb-1 flex items-center">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 mr-2"></span>
                    {smoothingTechnique === SmoothingTechnique.MOVING_AVERAGE && `Moving Average`}
                    {smoothingTechnique === SmoothingTechnique.EXPONENTIAL_SMOOTHING && `Exponential Smoothing`}
                    {smoothingTechnique === SmoothingTechnique.MEDIAN_FILTER && `Median Filter`}
                  </h4>
                  <p className="text-gray-300">
                    Helps reveal underlying patterns by reducing noise. {
                      smoothingTechnique === SmoothingTechnique.MOVING_AVERAGE 
                        ? `It averages the last ${smoothingParameter} data points.` 
                        : smoothingTechnique === SmoothingTechnique.EXPONENTIAL_SMOOTHING
                          ? `It weights recent observations higher with α=${alphaValue}.`
                          : `It removes outliers by taking the median of ${smoothingParameter * 2 - 1} surrounding points.`
                    }
                  </p>
                </div>
              </div>
            )}
            
            {showPureSignal && (
              <div className="flex items-start border-l-2 border-indigo-400/70 pl-3 py-1">
                <div className="flex-1">
                  <h4 className="font-medium text-indigo-400 mb-1 flex items-center">
                    <span className="h-2 w-2 rounded-full bg-indigo-400 mr-2"></span>
                    True Signal
                  </h4>
                  <p className="text-gray-300">
                    Is the underlying pattern (normally hidden from view in real-world data). Smoothing techniques attempt to recover this.
                  </p>
                </div>
              </div>
            )}
            
            <div className="mt-4 pt-3 border-t border-gray-700/50">
              <p className="text-gray-400 italic flex items-center">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Adjust the controls to see how different signal-to-noise ratios, patterns, and smoothing techniques affect your ability to identify real insights.
              </p>
            </div>
          </div>
        </div>
        
      </div>
    </div>
  )
}

/**
 * Signal vs. Noise Interactive Chart Component
 * Demonstrates how to distinguish meaningful patterns from random fluctuations
 * 
 * @returns {JSX.Element} Rendered component
 */
export default function SignalNoiseChart(): JSX.Element {
  return (
    <ChartVisibilityBoundary aria-label="Signal versus noise interactive chart">
      {({ hasEntered, isIntersecting }) =>
        hasEntered ? <SignalNoiseChartContent isActive={isIntersecting} /> : null
      }
    </ChartVisibilityBoundary>
  )
}